/**
 * Default protocol checklist (from ServiceReports_Sample).
 * Structure: category -> { tasks: [{ task, completed }] }
 * Used as reminder for operators and visual proof for admins/clients.
 */
export type ChecklistCategory = {
  tasks: { task: string; completed: boolean }[]
}

export type ChecklistData = Record<string, ChecklistCategory>

function category(tasks: string[]): ChecklistCategory {
  return { tasks: tasks.map((task) => ({ task, completed: false })) }
}

/** Default checklist template matching the sample report structure. */
export const DEFAULT_CHECKLIST: ChecklistData = {
  "Entrance Area": category([
    "Dust TV",
    "Window sills",
    "Clean Baseboards",
    "Replace trash bags",
    "Clean coffee table",
    "Clean coffee machine",
    "Clean water fountain",
    "Dust and clean chairs",
    "Dust and polish tables",
    "Clean front door window",
    "Vacuum floor",
    "Mop floor",
  ]),
  "Front Counter": category([
    "Clorox phones",
    "Dust keyboards",
    "Dust computers front + back",
    "Dust and clean chairs",
    "Dust kitchen barn doors",
    "Dust frames glass and top",
    "Clorox trash cans",
    "Dust product shelves",
    "Vacuum floor",
    "Mop floor",
  ]),
  "Doctor's Offices": category([
    "Dust keyboards",
    "Dust computers front + back",
    "Clorox phones",
    "Dust and clean chairs",
    "Clorox trash cans",
    "Replace trash bags",
    "Vacuum floor",
    "Mop floor",
  ]),
  Kitchens: category([
    "Dust TV",
    "Dust keyboard",
    "Dust and clean chairs",
    "Clean table",
    "Dust top of fridge",
    "Clean microwave inside and out",
    "Clean dish shelf",
    "Clorox trash cans",
    "Replace trash bags",
    "Clorox phones",
    "Clean sink and area",
    "Clean second sink and area",
    "Clean back door window",
    "Vacuum floor",
    "Mop floor",
  ]),
  Lab: category([
    "Vacuum floor",
    "Mop floor",
    "Dust counters",
    "Clean sink",
  ]),
  Bathrooms: category([
    "Clean sink",
    "Clean mirror",
    "Clorox trash can",
    "Replace trash bags",
    "Replace toilet paper (if needed)",
    "Vacuum floor",
    "Mop floor",
  ]),
  "Patient Rooms": category([
    "Dust barn doors inside and out",
    "Vacuum behind barn doors",
    "Dust screens thoroughly",
    "Dust chairs, remove any marks",
    "Clean sink (if needed)",
    "Replace trash bags",
    "Clean mirror",
    "Vacuum floor",
    "Clean chair bases",
    "Mop floor",
  ]),
  "Sterilization Rooms": category([
    "Clean sink",
    "Dust counters",
    "Vacuum floor",
    "Mop floor",
  ]),
  "X-ray Area": category([
    "Dust screens thoroughly",
    "Clean glass windows",
    "Replace trash bag",
    "Clorox trash can",
    "Vacuum floor",
    "Mop floor",
  ]),
  "Back Area": category([
    "Dust screens thoroughly",
    "Replace trash bag",
    "Clorox trash can",
    "Vacuum floor",
    "Mop floor",
  ]),
  "Storage Room": category([
    "Vacuum floor",
    "Mop floor",
  ]),
}

/** Returns a truly empty checklist — no categories, no tasks. */
export function createEmptyChecklist(): ChecklistData {
  return {}
}

/** Deep clone the default sample checklist (all unchecked). */
export function createDefaultChecklist(): ChecklistData {
  return JSON.parse(JSON.stringify(DEFAULT_CHECKLIST)) as ChecklistData
}

/**
 * Count tasks with completed: true in a checklist (from activities.checklist).
 * Handles null/undefined, empty object, and malformed structure.
 */
export function countCompletedTasks(checklist: unknown): number {
  if (checklist == null || typeof checklist !== "object" || Array.isArray(checklist)) return 0
  const obj = checklist as Record<string, unknown>
  let count = 0
  for (const key of Object.keys(obj)) {
    const category = obj[key]
    if (category == null || typeof category !== "object" || Array.isArray(category)) continue
    const cat = category as Record<string, unknown>
    const tasks = cat.tasks
    if (!Array.isArray(tasks)) continue
    for (const t of tasks) {
      if (t != null && typeof t === "object" && (t as { completed?: boolean }).completed === true) count += 1
    }
  }
  return count
}

/**
 * Count all tasks in a checklist (completed or not). Same structure as countCompletedTasks.
 * Used to normalize tasks to 0–100 for Luster Score: (completed / total) * 100.
 */
export function countTotalTasks(checklist: unknown): number {
  if (checklist == null || typeof checklist !== "object" || Array.isArray(checklist)) return 0
  const obj = checklist as Record<string, unknown>
  let count = 0
  for (const key of Object.keys(obj)) {
    const category = obj[key]
    if (category == null || typeof category !== "object" || Array.isArray(category)) continue
    const cat = category as Record<string, unknown>
    const tasks = cat.tasks
    if (!Array.isArray(tasks)) continue
    for (const t of tasks) {
      if (t != null && typeof t === "object") count += 1
    }
  }
  return count
}
